/**
 * CoverageModelSelector — Reusable component for selecting insurance coverage models
 *
 * Fetches coverage models from the API and displays them as selectable cards.
 * Each card shows included guarantees with per-guarantee limit/deductible customization.
 *
 * Props:
 *   value          - { coverageModelId, overrides } — current selection
 *   onChange        - (value) => void — called with { coverageModelId, overrides }
 *   contractId     - (optional) contract ID for loading existing coverage
 *   compact        - (optional) boolean — compact mode for inline use
 *   disabled       - (optional) boolean
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { insuranceModuleAPI } from '../../../api/insurance';

// Category colors
const CATEGORY_COLORS = {
  mandatory: 'bg-red-100 text-red-800',
  dommages: 'bg-orange-100 text-orange-800',
  personnes: 'bg-blue-100 text-blue-800',
  services: 'bg-green-100 text-green-800',
  fleet: 'bg-purple-100 text-purple-800',
};

export default function CoverageModelSelector({ value = {}, onChange, contractId, compact = false, disabled = false }) {
  const { t } = useTranslation();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedModel, setExpandedModel] = useState(null);

  const selectedModelId = value?.coverageModelId || null;
  const overrides = value?.overrides || {};

  // Fetch models on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const result = await insuranceModuleAPI.getModels();
        if (!cancelled) {
          setModels(result.data || result || []);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-expand selected model
  useEffect(() => {
    if (selectedModelId) setExpandedModel(selectedModelId);
  }, [selectedModelId]);

  const handleSelectModel = useCallback((modelId) => {
    if (disabled) return;
    if (selectedModelId === modelId) {
      // Deselect
      onChange?.({ coverageModelId: null, coverageModelName: null, coverageModelLevel: null, overrides: {} });
    } else {
      const model = models.find(m => m.id === modelId);
      onChange?.({
        coverageModelId: modelId,
        coverageModelName: model?.name || null,
        coverageModelLevel: model?.level || null,
        overrides: {},
      });
    }
  }, [selectedModelId, models, onChange, disabled]);

  const selectedModelName = value?.coverageModelName || null;
  const selectedModelLevel = value?.coverageModelLevel || null;

  const handleOverrideChange = useCallback((guaranteeCode, field, val) => {
    if (disabled) return;
    const newOverrides = { ...overrides };
    if (!newOverrides[guaranteeCode]) newOverrides[guaranteeCode] = {};
    newOverrides[guaranteeCode][field] = val;
    onChange?.({ coverageModelId: selectedModelId, coverageModelName: selectedModelName, coverageModelLevel: selectedModelLevel, overrides: newOverrides });
  }, [overrides, selectedModelId, selectedModelName, selectedModelLevel, onChange, disabled]);

  const handleToggleOptional = useCallback((guaranteeCode, currentlyIncluded) => {
    if (disabled) return;
    const newOverrides = { ...overrides };
    if (!newOverrides[guaranteeCode]) newOverrides[guaranteeCode] = {};
    newOverrides[guaranteeCode].included = !currentlyIncluded;
    onChange?.({ coverageModelId: selectedModelId, coverageModelName: selectedModelName, coverageModelLevel: selectedModelLevel, overrides: newOverrides });
  }, [overrides, selectedModelId, selectedModelName, selectedModelLevel, onChange, disabled]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
        <span className="ml-2 text-sm text-gray-500">{t('insuranceConfig.coverageSelector.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
        {t('insuranceConfig.coverageSelector.loadError', { error })}
      </div>
    );
  }

  if (models.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-700">
        {t('insuranceConfig.coverageSelector.empty')}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Model Cards */}
      <div className={compact ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-2 lg:grid-cols-4 gap-3'}>
        {models.map(model => {
          const isSelected = selectedModelId === model.id;
          const includedCount = (model.guarantees || []).filter(g => g.inclusion_type === 'included').length;
          const optionalCount = (model.guarantees || []).filter(g => g.inclusion_type === 'optional').length;

          return (
            <div
              key={model.id}
              className={`relative rounded-lg border-2 p-3 cursor-pointer transition-all ${
                isSelected
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
              } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
              onClick={() => handleSelectModel(model.id)}
            >
              {/* Recommended badge */}
              {model.is_recommended ? (
                <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {t('insuranceConfig.coverageSelector.recommended')}
                </span>
              ) : null}

              {/* Icon + Name */}
              <div className="flex items-center gap-2 mb-1">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                  style={{ backgroundColor: model.color_code || '#6B7280' }}
                >
                  {model.name?.charAt(0) || '?'}
                </div>
                <div>
                  <div className="font-semibold text-sm">{model.name}</div>
                  <div className="text-xs text-gray-500">{model.short_description}</div>
                </div>
              </div>

              {/* Guarantee count */}
              <div className="text-xs text-gray-400 mt-1">
                {optionalCount > 0
                  ? t('insuranceConfig.coverageSelector.summaryWithOptional', { included: includedCount, optional: optionalCount })
                  : t('insuranceConfig.coverageSelector.summaryIncluded', { count: includedCount })}
              </div>

              {/* Selection indicator */}
              {isSelected && (
                <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Guarantee Details (expanded view for selected model) */}
      {selectedModelId && !compact && (() => {
        const model = models.find(m => m.id === selectedModelId);
        if (!model?.guarantees?.length) return null;

        const included = model.guarantees.filter(g => g.inclusion_type === 'included');
        const optional = model.guarantees.filter(g => g.inclusion_type === 'optional');

        return (
          <div className="bg-white border border-gray-200 rounded-lg p-4 mt-2">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
              {t('insuranceConfig.coverageSelector.guaranteesOf', { name: model.name })}
            </h4>

            {/* Included Guarantees */}
            {included.length > 0 && (
              <div className="mb-3">
                <div className="text-xs font-medium text-gray-500 uppercase mb-1">{t('insuranceConfig.coverageSelector.includedTitle')}</div>
                <div className="space-y-1">
                  {included.map(g => (
                    <GuaranteeRow
                      key={g.code}
                      guarantee={g}
                      override={overrides[g.code]}
                      onOverrideChange={(field, val) => handleOverrideChange(g.code, field, val)}
                      isIncluded={true}
                      disabled={disabled}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Optional Guarantees */}
            {optional.length > 0 && (
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase mb-1">{t('insuranceConfig.coverageSelector.optionalTitle')}</div>
                <div className="space-y-1">
                  {optional.map(g => {
                    const isEnabled = overrides[g.code]?.included === true;
                    return (
                      <GuaranteeRow
                        key={g.code}
                        guarantee={g}
                        override={overrides[g.code]}
                        onOverrideChange={(field, val) => handleOverrideChange(g.code, field, val)}
                        isIncluded={isEnabled}
                        isOptional={true}
                        onToggle={() => handleToggleOptional(g.code, isEnabled)}
                        disabled={disabled}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

/** Single guarantee row with optional limit/deductible editing */
function GuaranteeRow({ guarantee, override = {}, onOverrideChange, isIncluded, isOptional, onToggle, disabled }) {
  const { t } = useTranslation();
  const categoryClass = CATEGORY_COLORS[guarantee.category] || 'bg-gray-100 text-gray-800';
  const effectiveDeductible = override?.deductible_amount ?? guarantee.custom_deductible ?? guarantee.default_deductible;
  const effectiveLimit = override?.limit_amount ?? guarantee.custom_limit ?? guarantee.default_limit;

  return (
    <div className={`flex items-center gap-2 px-2 py-1.5 rounded ${isIncluded ? 'bg-gray-50' : 'bg-gray-50 opacity-50'}`}>
      {/* Toggle for optional */}
      {isOptional && (
        <button
          type="button"
          className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
            isIncluded ? 'bg-blue-500 border-blue-500 text-white' : 'border-gray-300 bg-white'
          }`}
          onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
          disabled={disabled}
        >
          {isIncluded && (
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>
      )}

      {/* Name + category badge */}
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-gray-800">{guarantee.name}</span>
        <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${categoryClass}`}>
          {t(`insuranceConfig.categories.${guarantee.category}`, { defaultValue: guarantee.category })}
        </span>
      </div>

      {/* Deductible */}
      {isIncluded && (
        <div className="flex items-center gap-1">
          {effectiveDeductible !== null && effectiveDeductible !== undefined ? (
            <div className="text-xs text-gray-500">
              <span className="text-gray-400">{t('insuranceConfig.coverageSelector.deductible')}</span>{' '}
              <input
                type="number"
                className="w-16 text-xs border border-gray-200 rounded px-1 py-0.5 text-right"
                value={override?.deductible_amount ?? effectiveDeductible ?? ''}
                onChange={(e) => onOverrideChange('deductible_amount', e.target.value ? parseFloat(e.target.value) : null)}
                placeholder="0"
                disabled={disabled}
                onClick={(e) => e.stopPropagation()}
              />
              <span className="text-gray-400 ml-0.5">€</span>
            </div>
          ) : null}

          {/* Limit */}
          {effectiveLimit !== null && effectiveLimit !== undefined ? (
            <div className="text-xs text-gray-500 ml-2">
              <span className="text-gray-400">{t('insuranceConfig.coverageSelector.limit')}</span>{' '}
              <input
                type="number"
                className="w-16 text-xs border border-gray-200 rounded px-1 py-0.5 text-right"
                value={override?.limit_amount ?? effectiveLimit ?? ''}
                onChange={(e) => onOverrideChange('limit_amount', e.target.value ? parseFloat(e.target.value) : null)}
                placeholder="0"
                disabled={disabled}
                onClick={(e) => e.stopPropagation()}
              />
              <span className="text-gray-400 ml-0.5">€</span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
